import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Calendar, 
  Clock, 
  DollarSign, 
  FileText,
  CalendarRange,
  Bell,
  CheckCircle,
  XCircle,
  AlertCircle,
  LogOut,
  QrCode
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StaffDashboardPage = () => {
  const { user, token, userPermissions } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    attendance: null,
    leaves: [],
    notifications: []
  });

  const fetchDashboardData = async () => {
    try {
      // Fetch attendance summary
      const today = new Date().toISOString().split('T')[0];
      
      // We can add more API calls here as needed
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Get accessible modules for quick access cards
  const getQuickAccessCards = () => {
    const cards = [];
    const modules = userPermissions?.modules || [];

    if (modules.includes('attendance') || modules.includes('hr')) {
      cards.push({
        title: 'My Attendance',
        icon: Calendar,
        path: '/staff/my-attendance',
        color: 'bg-blue-500',
        description: 'View your attendance records'
      });
    }

    if (modules.includes('hr')) {
      cards.push({
        title: 'My Salary',
        icon: DollarSign,
        path: '/staff/my-salary',
        color: 'bg-green-500',
        description: 'View salary slips'
      });
      cards.push({
        title: 'Apply Leave',
        icon: CalendarRange,
        path: '/staff/leave',
        color: 'bg-amber-500',
        description: 'Request leave'
      });
    }

    // Gate Pass for Warden - check if hostel module available
    if (modules.includes('hostel') || modules.includes('gate_pass') || modules.includes('hr')) {
      cards.push({
        title: 'Gate Pass',
        icon: LogOut,
        path: '/gate-pass',
        color: 'bg-orange-500',
        description: 'Approve student gate passes'
      });
      // QR Scanner for quick gate scanning
      cards.push({
        title: 'Gate Scanner',
        icon: QrCode,
        path: '/gate-pass?scanner=true',
        color: 'bg-blue-600',
        description: 'Scan student QR codes'
      });
    }

    if (modules.includes('fees')) {
      cards.push({
        title: 'Fee Management',
        icon: DollarSign,
        path: '/fee-management',
        color: 'bg-emerald-500',
        description: 'Manage student fees'
      });
    }

    if (modules.includes('accounts')) {
      cards.push({
        title: 'Accounts',
        icon: FileText,
        path: '/accounts',
        color: 'bg-purple-500',
        description: 'Financial records'
      });
    }

    if (modules.includes('library')) {
      cards.push({
        title: 'Library',
        icon: FileText,
        path: '/library',
        color: 'bg-indigo-500',
        description: 'Manage books'
      });
    }

    if (modules.includes('students')) {
      cards.push({
        title: 'Students',
        icon: FileText,
        path: '/students',
        color: 'bg-cyan-500',
        description: 'Student records'
      });
    }

    if (modules.includes('reports')) {
      cards.push({
        title: 'Reports',
        icon: FileText,
        path: '/reports',
        color: 'bg-rose-500',
        description: 'View reports'
      });
    }

    return cards.slice(0, 6); // Show max 6 cards
  };

  const quickAccessCards = getQuickAccessCards();

  return (
    <DashboardLayout title="Staff Dashboard">
      <div className="space-y-6" data-testid="staff-dashboard">
        {/* Welcome Section */}
        <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-foreground">
                  Welcome, {user?.name}!
                </h2>
                <p className="text-muted-foreground mt-1">
                  Role: {userPermissions?.role_name || 'Staff'}
                </p>
              </div>
              <div className="hidden md:flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Module Access Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              Your Access Permissions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {userPermissions?.modules?.map((module) => (
                <span 
                  key={module}
                  className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm capitalize"
                >
                  {module.replace('_', ' ')}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick Access Cards */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Quick Access</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {quickAccessCards.map((card, index) => {
              const Icon = card.icon;
              return (
                <Card 
                  key={index}
                  className="cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => navigate(card.path)}
                  data-testid={`quick-access-${card.title.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className={`${card.color} p-3 rounded-lg`}>
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground">{card.title}</h4>
                        <p className="text-sm text-muted-foreground">{card.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Empty State if no modules */}
        {quickAccessCards.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center">
              <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Modules Assigned</h3>
              <p className="text-muted-foreground">
                Please contact your administrator to get access to modules.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default StaffDashboardPage;
